import { Component, ComponentInterface, h, Host, State } from '@stencil/core';
import { MaintenanceInfo, MaintenanceInfoActiveState } from '../../../models/MaintenanceInfo';
import getEnv, { IEnv } from '../../../utils/env';

/**
 * @internal
 */
@Component({
  tag: 'bahf-maintenance-info',
  styleUrl: 'bahf-maintenance-info.scss',
  assetsDirs: ['../assets'],
  shadow: false,
  scoped: true,
})
export class BahfMaintenanceInfo implements ComponentInterface {
  private env: IEnv = getEnv();
  private maintenanceElem: HTMLDivElement;

  @State() private isMaintenanceInfoActive = false;
  private maintenanceInfo: MaintenanceInfo;

  constructor() {
    this.setMaintenanceInfoClosedState = this.setMaintenanceInfoClosedState.bind(this);
  }

  componentWillLoad() {
    this.fetchMaintenanceInfo()
      .then(async (response) => {
        const info: MaintenanceInfo = await response.json();
        this.maintenanceInfo = info;
        this.isMaintenanceInfoActive = this.validateMaintenanceInfo(info);
      })
      .catch((error) => {
        console.warn(error);
      });
  }

  componentDidLoad() {
    this.isMaintenanceInfoActive && this.initAlert();
  }

  private initAlert() {
    this.maintenanceElem.querySelector('.close').addEventListener('click', (e) => {
      e.preventDefault();
      this.maintenanceElem['bsAlert'] =
        window.dpl.bs.Alert.getInstance(this.maintenanceElem) ||
        new window.dpl.bs.Alert(this.maintenanceElem);
      this.maintenanceElem['bsAlert'].close();
    });
  }

  private fetchMaintenanceInfo(): Promise<Response> {
    const maintenanceInfoUrl = this.env.maintenanceInfoUrl;
    return fetch(maintenanceInfoUrl, {
      method: 'GET',
      headers: {
        'x-api-key': this.env.apiClientId
      }
    }).then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }
      return response;
    });
  }

  validateMaintenanceInfo(info: MaintenanceInfo) {
    if (sessionStorage.getItem('maintenanceInfoState') === 'closed') {
      return false;
    }

    switch (info.state) {
      case MaintenanceInfoActiveState.ACTIVE:
        return true;
      case MaintenanceInfoActiveState.INACTIVE:
        return false;
      case MaintenanceInfoActiveState.ACTIVE_FROM:
        return this.validateMaintenanceFromDate(info.fromDate);
      case MaintenanceInfoActiveState.ACTIVE_FROM_TO:
        return this.validateMaintenanceFromToDate(info.fromDate, info.toDate);
      default:
        console.warn(`Aktivstatus des Wartungshinweises unbekannt: ${info.state}`);
        return false;
    }
  }

  private validateMaintenanceFromDate(fromDate: string) {
    if (!fromDate) return false;
    const today = new Date();
    const startDate = new Date(fromDate);
    return today >= startDate;
  }

  private validateMaintenanceFromToDate(fromDate: string, toDate: string) {
    if (!fromDate || !toDate) return false;
    const today = new Date();
    const startDate = new Date(fromDate);
    const endDate = new Date(toDate);
    return today >= startDate && today <= endDate;
  }

  private setMaintenanceInfoClosedState() {
    this.isMaintenanceInfoActive = false;
    sessionStorage.setItem('maintenanceInfoState', 'closed');
  }

  render() {
    return this.isMaintenanceInfoActive && (
      <Host>
        <div
          data-testid="bahf-maintenance-info-container"
          id="maintenance-info-container"
          class="alert alert-info alert-dismissible"
          role="note"
          ref={(el) => {
            this.maintenanceElem = el;
          }}
        >
          <span class="ba-heading">Wartungsarbeiten:</span>
          <p class="ba-copytext" innerHTML={this.maintenanceInfo.text}></p>
          <button
            data-testid="bahf-maintenance-info-container-btn-close"
            type="button"
            class="close"
            data-bs-dismiss="alert"
            aria-label="Wartungshinweis schließen"
            title="Wartungshinweis schließen"
            onClick={this.setMaintenanceInfoClosedState}
          ></button>
        </div>
      </Host>
    );
  }
}
